import { useCallback, useEffect, useState } from 'react'

import { DEFAULT_LISTINGS_SORTING } from '@lib/components'
import type { ListingsSorting, TransactionType } from '@lib/components'

export type EntityFiltersState = {
  searchQuery: string
  transactionType?: TransactionType
  sort: ListingsSorting
}

const createInitialFilters = (
  transactionType?: TransactionType
): EntityFiltersState => ({
  searchQuery: '',
  transactionType,
  sort: DEFAULT_LISTINGS_SORTING,
})

export const useSelectEntityFilters = (
  conversationId: number,
  defaultTransactionType?: TransactionType
) => {
  const [listingFilters, setListingFilters] = useState<EntityFiltersState>(() =>
    createInitialFilters(defaultTransactionType)
  )
  const [projectFilters, setProjectFilters] = useState<EntityFiltersState>(() =>
    createInitialFilters(defaultTransactionType)
  )

  useEffect(() => {
    setListingFilters(createInitialFilters(defaultTransactionType))
    setProjectFilters(createInitialFilters(defaultTransactionType))
  }, [conversationId, defaultTransactionType])

  const handleListingSearchFilterChange = useCallback((value: string) => {
    setListingFilters((prev) => ({ ...prev, searchQuery: value }))
  }, [])

  const handleListingTransactionFilterChange = useCallback(
    (value: TransactionType | undefined) => {
      setListingFilters((prev) => ({ ...prev, transactionType: value }))
    },
    []
  )

  const handleListingSortFilterChange = useCallback(
    (value: ListingsSorting) => {
      setListingFilters((prev) => ({ ...prev, sort: value }))
    },
    []
  )

  const handleProjectSearchFilterChange = useCallback((value: string) => {
    setProjectFilters((prev) => ({ ...prev, searchQuery: value }))
  }, [])

  const handleProjectTransactionFilterChange = useCallback(
    (value: TransactionType | undefined) => {
      setProjectFilters((prev) => ({ ...prev, transactionType: value }))
    },
    []
  )

  const handleProjectSortFilterChange = useCallback(
    (value: ListingsSorting) => {
      setProjectFilters((prev) => ({ ...prev, sort: value }))
    },
    []
  )

  return {
    listingFilters,
    projectFilters,
    handleListingSearchFilterChange,
    handleListingTransactionFilterChange,
    handleListingSortFilterChange,
    handleProjectSearchFilterChange,
    handleProjectTransactionFilterChange,
    handleProjectSortFilterChange,
  }
}
